"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { getCountryLabels } from "@/app/[lang]/[workspaceId]/sa/_utils/country";
import {
  DeviceLabels,
  SearchAnalyticsDataDateTypeLabels,
  SearchAnalyticsFilterLabels,
  SearchAnalyticsRawDataKindLabels,
  SearchAnalyticsSearchTypeLabels,
} from "@/app/[lang]/[workspaceId]/sa/constants";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { UpdateSearchAnalyticsViewInput } from "./schema";

const ProjectDataQuery = graphql(`
  query SaViewUpdateConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        name
        searchAnalytics {
          id
          __typename
          queryGroups { id __typename name }
          urlGroups { id __typename name }
        }
      }
    }
  }
`);

function toProjectIdFromViewId(viewId: string): string | null {
  try {
    const decoded = atob(viewId);
    const parts = decoded.split("/");
    // SearchAnalyticsView:customers/{org}/projects/{project}/search_analytics/{sa}/views/{view}
    const orgDb = parts[1];
    const projectDb = parts[3];
    if (!orgDb || !projectDb) return null;
    const rawProject = `Project:customers/${orgDb}/projects/${projectDb}`;
    return btoa(rawProject);
  } catch {
    return null;
  }
}

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateSearchAnalyticsViewConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const isTarget =
    isToolUIPart(part) && getToolName(part) === "update_search_analytics_view";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<UpdateSearchAnalyticsViewInput>;

  const projectId = input.id ? toProjectIdFromViewId(input.id) : null;
  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId ?? "" },
    pause: !(isTarget && isInputAvailable && projectId),
  });
  const project =
    data?.node?.__typename === "SearchAnalyticsProject" ? data.node : null;
  const projectName = project?.name ?? "Unknown";
  const queryGroups = project?.searchAnalytics?.queryGroups ?? [];
  const queryGroupNames = Object.fromEntries(
    queryGroups.map((group) => [group.id, group.name]),
  );
  const urlGroups = project?.searchAnalytics?.urlGroups ?? [];
  const urlGroupNames = Object.fromEntries(
    urlGroups.map((group) => [group.id, group.name]),
  );

  if (!isTarget || !isInputAvailable) return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to update this Search Analytics view?</Trans>
      </div>
      <ul className="mb-4 list-disc pl-5 space-y-1">
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName}</span>
            )}
          </li>
        ) : null}

        {typeof input.name === "string" ? (
          <li>
            <Trans>Name</Trans>:{" "}
            <span className="font-medium">{input.name}</span>
          </li>
        ) : null}
        {typeof input.description === "string" ? (
          <li>
            <Trans>Description</Trans>:{" "}
            <span className="font-normal">{input.description}</span>
          </li>
        ) : null}
        {input.replaceViewSettings === true ? (
          <li>
            <Trans>Saved filter replacement</Trans>:{" "}
            <span className="font-medium">
              <Trans>Replace omitted saved filters</Trans>
            </span>
          </li>
        ) : null}

        {input.dataDate ? (
          <li>
            <Trans>Saved date</Trans>:{" "}
            <span className="font-medium">
              {i18n._(SearchAnalyticsDataDateTypeLabels[input.dataDate.type])}
            </span>
            {input.dataDate.type === "CUSTOM" && input.dataDate.dateRange ? (
              <span className="ml-2 text-muted-foreground">
                ({input.dataDate.dateRange.start} -{" "}
                {input.dataDate.dateRange.end})
              </span>
            ) : null}
          </li>
        ) : null}

        {"query" in input ? (
          <li>
            <Trans>Query</Trans>:{" "}
            <span className="font-medium">{input.query ?? "(null)"}</span>
            {"queryFilterType" in input && input.queryFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(SearchAnalyticsFilterLabels[input.queryFilterType])}
              </span>
            ) : null}
          </li>
        ) : null}

        {"url" in input ? (
          <li>
            <Trans>URL</Trans>:{" "}
            <span className="font-medium break-all">
              {input.url ?? "(null)"}
            </span>
            {"urlFilterType" in input && input.urlFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(SearchAnalyticsFilterLabels[input.urlFilterType])}
              </span>
            ) : null}
          </li>
        ) : null}

        {"queryGroupIDs" in input ? (
          <li>
            <Trans>Query Groups</Trans>:{" "}
            <span className="font-medium">
              {(input.queryGroupIDs ?? [])
                .map((id) => queryGroupNames[id!] || id)
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"urlGroupIDs" in input ? (
          <li>
            <Trans>URL Groups</Trans>:{" "}
            <span className="font-medium">
              {(input.urlGroupIDs ?? [])
                .map((id) => urlGroupNames[id!] || id)
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"searchTypes" in input ? (
          <li>
            <Trans>Search type</Trans>:{" "}
            <span className="font-medium">
              {(input.searchTypes ?? [])
                .map((t) => i18n._(SearchAnalyticsSearchTypeLabels[t]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"devices" in input ? (
          <li>
            <Trans>Devices</Trans>:{" "}
            <span className="font-medium">
              {(input.devices ?? [])
                .map((d) => i18n._(DeviceLabels[d]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"countries" in input ? (
          <li>
            <Trans>Country</Trans>:{" "}
            <span className="font-medium">
              {(input.countries ?? [])
                .map((c) => {
                  const countryLabels = getCountryLabels();
                  return c in countryLabels
                    ? countryLabels[c as keyof typeof countryLabels]
                    : c;
                })
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"rawKinds" in input ? (
          <li>
            <Trans>Search Appearance</Trans>:{" "}
            <span className="font-medium">
              {(input.rawKinds ?? [])
                .map((k) => i18n._(SearchAnalyticsRawDataKindLabels[k]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"rawKindsNot" in input ? (
          <li>
            <Trans>Search Appearances to exclude</Trans>:{" "}
            <span className="font-medium">
              {(input.rawKindsNot ?? [])
                .map((k) => i18n._(SearchAnalyticsRawDataKindLabels[k]))
                .join(", ") || "(null)"}
            </span>
          </li>
        ) : null}

        {"isAnonymousQuery" in input ? (
          <li>
            <Trans>Anonymous Query</Trans>:{" "}
            <span className="font-medium">
              {input.isAnonymousQuery === null
                ? "(null)"
                : input.isAnonymousQuery
                  ? "true"
                  : "false"}
            </span>
          </li>
        ) : null}

        {input.rawDataOrderBy ? (
          <li>
            <Trans>Order by</Trans>:{" "}
            <span className="font-medium">
              {input.rawDataOrderBy.field} {input.rawDataOrderBy.direction}
            </span>
          </li>
        ) : null}
        {input.rawDataGroupBy ? (
          <li>
            <Trans>Group by</Trans>:{" "}
            <span className="font-medium">
              {input.rawDataGroupBy.join(", ")}
            </span>
          </li>
        ) : null}
      </ul>
      <div className="mb-3 text-xs text-muted-foreground">
        <Trans>
          Only provided fields will be updated. Omitted fields remain unchanged.
        </Trans>
      </div>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
